import { useState, useEffect } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import useAuth from '@sdk/authentication/useAuth';
import Column from '@sdk/components/ui/spacing/Column';
import Text14 from '@sdk/components/ui/typography/Text14';
import TextField from '@sdk/components/forms/TextField';
import gettext from '@sdk/lang/gettext';
import { Feature } from '@sdk/common/features';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import validateNumber, {
  ValidateOptions,
  ValidNumberOption,
} from '@sdk/utils/validation/validateNumber';

import CollapsibleSettingsSection from '../CollapsibleSettingsSection';
import RowSpaceBetween from '../RowSpaceBetween';
import SettingsSection from '../SettingsSection';
import SettingsSwitcherRow from '../SettingsSwitcherRow';
import { TabType } from '../../types';
import { useSettingsErrorsContext } from '../../SettingsErrorContext';
import { FIELD_WIDTH_205 } from '../../constants';
import { useUserOrgSettings } from '../../UserOrgSettingsContext';

const StyledTextField = styled(TextField)`
  max-width: ${FIELD_WIDTH_205}px;
`;

const TimeOffRequest: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { userOrgSettings, onUserOrgSettingsChange } = useUserOrgSettings();
  const { isOrgFeatureEnabled, currentUser } = useAuth();
  const [leaveRequestDisabledBeforeDays, setLeaveRequestDisabledBeforeDays] = useState(
    () => userOrgSettings.leaveRequestDisabledBeforeDays
  );
  const { toggleError, isFieldValid } = useSettingsErrorsContext();

  useEffect(() => {
    setLeaveRequestDisabledBeforeDays(userOrgSettings.leaveRequestDisabledBeforeDays);
  }, [userOrgSettings.leaveRequestDisabledBeforeDays]);

  const leaveRequestsBeforeDaysEnabledChanged = (leaveRequestsBeforeDaysEnabled: boolean): void => {
    if (leaveRequestsBeforeDaysEnabled && !userOrgSettings.leaveRequestDisabledBeforeDays) {
      onUserOrgSettingsChange({ leaveRequestDisabledBeforeDays: 1 });
      toggleError(TabType.timeOff, 'leaveRequestDisabledBeforeDays', false);
      return;
    }

    if (!leaveRequestsBeforeDaysEnabled) {
      onUserOrgSettingsChange({ leaveRequestDisabledBeforeDays: 0 });
      toggleError(TabType.timeOff, 'leaveRequestDisabledBeforeDays', false);
    }
  };

  const validateNumberOfDaysInAdvance = (value: number | string): boolean => {
    return validateNumber(value, {
      [ValidateOptions.validNumber]: ValidNumberOption.positive,
      [ValidateOptions.integer]: true,
    });
  };

  return (
    <SettingsSection>
      <CollapsibleSettingsSection
        withSeparator
        isActive={isOrgFeatureEnabled(Feature.timeOff) && userOrgSettings.usersCanRequestLeave}
        headerItem={
          <SettingsSwitcherRow
            disabled={!currentUser.isAdmin || !isOrgFeatureEnabled(Feature.timeOff)}
            subtitle={gettext('Allow employees to request time off.')}
            value={isOrgFeatureEnabled(Feature.timeOff) && userOrgSettings.usersCanRequestLeave}
            onChange={(value): void => onUserOrgSettingsChange({ usersCanRequestLeave: value })}
          />
        }
        contentItem={
          <>
            <SettingsSwitcherRow
              disabled={!currentUser.isAdmin}
              pBottom={20}
              value={userOrgSettings.leaveRequestRequiresApproval}
              onChange={(value): void =>
                onUserOrgSettingsChange({ leaveRequestRequiresApproval: value })
              }
              title={gettext('Approval')}
              subtitle={gettext('Require manager approval of time off requests.')}
            />

            <CollapsibleSettingsSection
              isActive={leaveRequestDisabledBeforeDays !== 0}
              headerItem={
                <SettingsSwitcherRow
                  disabled={!currentUser.isAdmin}
                  pBottom={20}
                  value={leaveRequestDisabledBeforeDays !== 0}
                  onChange={(value): void => {
                    setLeaveRequestDisabledBeforeDays(value ? 1 : 0);
                    leaveRequestsBeforeDaysEnabledChanged(value);
                  }}
                  title={gettext('Cutoff time')}
                  subtitle={gettext('Time off requests must be submitted in advance.')}
                />
              }
              contentItem={
                <Column pBottom={20}>
                  <RowSpaceBetween>
                    <Column>
                      <Text14 semiBold themeColor={ThemeColorType.gray}>
                        {gettext('Number of days in advance')}
                      </Text14>
                      <Text14 themeColor={ThemeColorType.gray}>
                        {gettext(
                          'Indicate how many days in advance time off requests must be submitted:'
                        )}
                      </Text14>
                    </Column>
                    <StyledTextField
                      skipErrorMessage
                      disabled={!currentUser.isAdmin}
                      postfix={gettext('Days')}
                      value={userOrgSettings.leaveRequestDisabledBeforeDays}
                      onChange={(e): void => {
                        onUserOrgSettingsChange({
                          leaveRequestDisabledBeforeDays: e.currentTarget.value,
                        });
                        toggleError(
                          TabType.timeOff,
                          'leaveRequestDisabledBeforeDays',
                          !validateNumberOfDaysInAdvance(e.currentTarget.value)
                        );
                      }}
                      invalid={!isFieldValid('leaveRequestDisabledBeforeDays')}
                    />
                  </RowSpaceBetween>
                </Column>
              }
            />
          </>
        }
      />

      <SettingsSwitcherRow
        disabled={!currentUser.isAdmin}
        pBottom={20}
        value={userOrgSettings.leaveRequestRequiresReason}
        onChange={(value): void => onUserOrgSettingsChange({ leaveRequestRequiresReason: value })}
        title={gettext('Comments')}
        subtitle={gettext('Require comments for time off requests.')}
      />
    </SettingsSection>
  );
};

export default TimeOffRequest;
